import type { NarrativeGraph, NarrativeNode, PlatformNode, SubjectNode, ThreadNode } from '../api'

/**
 * Positions for the narrative graph — computed once per payload, never per frame.
 *
 * ## Why there is no force simulation here
 *
 * The obvious way to lay out a graph is to run a physics step every frame and let it settle.
 * That is O(n²) per frame, and this board is specified to hold thousands of threads: at four
 * thousand nodes a naive repulsion pass is sixteen million distance checks sixty times a
 * second, which is not a frame budget, it is a space heater. It is also *wrong* for this
 * screen — a settling layout means the same data draws differently on every visit, and an
 * operator who learned where a cluster sits yesterday finds it somewhere else today.
 *
 * So the layout is **deterministic and hierarchical**. The same payload always produces the
 * same picture, and the cost is paid once when the data arrives.
 *
 * ## The nesting is what makes the zoom mean something
 *
 * Threads sit inside their narrative's disc; narratives sit inside their subject's disc;
 * subjects sit inside the platform ring. Because containment is real rather than implied,
 * zooming into a subject genuinely descends into its narratives and then into its threads —
 * the map metaphor holds all the way down, and a node never jumps between parents.
 *
 * ## Angle carries meaning
 *
 * A subject is placed at the circular mean of the platforms its threads actually came from,
 * so a subject being driven on TikTok drifts toward the TikTok node on the ring. That is one
 * more fact the picture states without anybody having to read a number, and it costs nothing.
 */

const TAU = Math.PI * 2

/**
 * The world is a fixed square, and the canvas is a window onto it.
 *
 * ## Why the layout does not know the canvas size
 *
 * It used to. Positions were computed in pixels from the current width and height, which meant
 * every resize — opening the full-screen view, dragging the window, a rail appearing — produced
 * a DIFFERENT layout under an unchanged viewport. The reader was left looking at empty space
 * where their cluster had been a moment earlier, with no way to know it had moved rather than
 * vanished.
 *
 * So the world has fixed coordinates centred on the origin, and the canvas maps onto it through
 * the viewport's scale. A resize now changes only how much of the world is on screen, exactly
 * like resizing a map window. Every radius below is in world units for the same reason.
 */
export const RING_R = 1000

export interface PlatformPoint {
  node: PlatformNode
  x: number
  y: number
  angle: number
  /** Layout radius in world units. */
  r: number
}

export interface SubjectPoint {
  node: SubjectNode
  x: number
  y: number
  /** Disc radius — every narrative of this subject sits inside it. */
  r: number
  narratives: NarrativePoint[]
}

export interface NarrativePoint {
  node: NarrativeNode
  x: number
  y: number
  r: number
  threads: ThreadPoint[]
}

export interface ThreadPoint {
  node: ThreadNode
  x: number
  y: number
  r: number
}

export interface Layout {
  /** World-space centre of the ring. */
  cx: number
  cy: number
  ringR: number
  platforms: PlatformPoint[]
  subjects: SubjectPoint[]
  subjectById: Map<string, SubjectPoint>
  narrativeById: Map<string, NarrativePoint>
  threadById: Map<string, ThreadPoint>
}

/** Deterministic hash → the layout must not depend on insertion order or on Math.random. */
function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) / 4294967296
}

/**
 * Phyllotaxis packing — the sunflower spiral.
 *
 * Children fill their parent disc at even density with no overlap test and no iteration, which
 * is what keeps a narrative of nine hundred threads as cheap to place as one of nine. The
 * golden angle is what stops the spiral from forming visible spokes.
 */
function phyllotaxis(i: number, count: number, radius: number): { x: number, y: number } {
  const golden = Math.PI * (3 - Math.sqrt(5))
  const t = count <= 1 ? 0 : Math.sqrt(i / count)
  const a = i * golden
  return { x: Math.cos(a) * t * radius, y: Math.sin(a) * t * radius }
}

/** Circular mean of angles, weighted. Returns null when there is nothing to average. */
function meanAngle(entries: { angle: number, weight: number }[]): number | null {
  let sx = 0
  let sy = 0
  for (const e of entries) {
    sx += Math.cos(e.angle) * e.weight
    sy += Math.sin(e.angle) * e.weight
  }
  if (sx === 0 && sy === 0) return null
  return Math.atan2(sy, sx)
}

export interface LayoutOptions {
  /** Platform codes the reader has switched off — excluded from placement, not just dimmed. */
  hiddenPlatforms?: Set<string>
}

export function buildLayout(graph: NarrativeGraph, opts: LayoutOptions = {}): Layout {
  const hidden = opts.hiddenPlatforms ?? new Set<string>()
  const cx = 0
  const cy = 0
  const ringR = RING_R

  // ── platform ring ──────────────────────────────────────────────────────────
  // Every configured platform gets a slot, including the empty ones. A ring that hides its
  // quiet channels cannot show a channel going quiet — and a watch that silently stopped
  // returning results looks exactly like calm.
  const platformOrder = [...graph.platforms].sort((a, b) => a.code.localeCompare(b.code))
  const platforms: PlatformPoint[] = platformOrder.map((node, i) => {
    const angle = -Math.PI / 2 + (i / Math.max(1, platformOrder.length)) * TAU
    return {
      node,
      angle,
      x: cx + Math.cos(angle) * ringR,
      y: cy + Math.sin(angle) * ringR,
      r: RING_R * 0.028 + Math.sqrt(node.share) * RING_R * 0.024,
    }
  })
  const platformAngle = new Map(platforms.map((p) => [p.node.code, p.angle]))

  // ── group the payload ──────────────────────────────────────────────────────
  const visibleThread = (t: ThreadNode) => !hidden.has(t.platformCode)
  const threadsByNarrative = new Map<string, ThreadNode[]>()
  for (const t of graph.threads) {
    if (!visibleThread(t)) continue
    const list = threadsByNarrative.get(t.narrativeId)
    if (list) list.push(t)
    else threadsByNarrative.set(t.narrativeId, [t])
  }
  const narrativesBySubject = new Map<string, NarrativeNode[]>()
  for (const n of graph.narratives) {
    // A narrative whose every thread is on a hidden platform is not dimmed, it is gone. A
    // filter that leaves empty husks behind is a filter the reader has to mentally re-apply.
    if (!(threadsByNarrative.get(n.id)?.length ?? 0)) continue
    const list = narrativesBySubject.get(n.subjectId)
    if (list) list.push(n)
    else narrativesBySubject.set(n.subjectId, [n])
  }

  // ── subjects ───────────────────────────────────────────────────────────────
  const activeSubjects = graph.subjects.filter((s) => narrativesBySubject.has(s.id))
  const maxThreads = Math.max(1, ...activeSubjects.map((s) => s.threads))

  // Disc size is capped by BOTH a fraction of the ring and the space the other subjects need.
  // Without the second term a room watching two subjects draws two discs that swallow the ring
  // and overlap its channel labels — the picture stops being a map and becomes two blobs. The
  // first term is what stops a single-subject room from drawing one disc the size of the stage.
  const maxRadius = Math.min(ringR * 0.30, ringR * 0.66 / Math.sqrt(activeSubjects.length))

  const placed: SubjectPoint[] = activeSubjects.map((node, i) => {
    const own = narrativesBySubject.get(node.id) ?? []
    const weights = new Map<string, number>()
    for (const n of own) {
      for (const t of threadsByNarrative.get(n.id) ?? []) {
        weights.set(t.platformCode, (weights.get(t.platformCode) ?? 0) + 1)
      }
    }
    const entries = [...weights].flatMap(([code, weight]) => {
      const angle = platformAngle.get(code)
      return angle === undefined ? [] : [{ angle, weight }]
    })
    // Fall back to an even slot, jittered by id, when a subject has no platform signal at all
    // — a deterministic position beats a pile at the centre.
    const angle = meanAngle(entries)
      ?? (-Math.PI / 2 + (i / Math.max(1, activeSubjects.length)) * TAU)

    // Radius: busier subjects sit further out, toward the channels feeding them. The centre
    // stays clear so the ring's interior reads as a stage rather than as a pile.
    const load = Math.sqrt(node.threads / maxThreads)
    const dist = ringR * (0.26 + load * 0.34)
    const jitter = (hash(node.id) - 0.5) * 0.12
    return {
      node,
      x: cx + Math.cos(angle + jitter) * dist,
      y: cy + Math.sin(angle + jitter) * dist,
      r: Math.max(RING_R * 0.07, maxRadius * Math.sqrt(node.threads / maxThreads)),
      narratives: [],
    }
  })

  relax(placed, cx, cy, ringR)

  // ── narratives and threads, packed inside their parent ─────────────────────
  const narrativeById = new Map<string, NarrativePoint>()
  const threadById = new Map<string, ThreadPoint>()

  for (const subject of placed) {
    const own = (narrativesBySubject.get(subject.node.id) ?? [])
      .slice()
      // Biggest first so the spiral puts the mass near the centre of the parent disc, where
      // there is room for it.
      .sort((a, b) => (threadsByNarrative.get(b.id)?.length ?? 0)
        - (threadsByNarrative.get(a.id)?.length ?? 0))
    const totalThreads = own.reduce(
      (sum, n) => sum + (threadsByNarrative.get(n.id)?.length ?? 0), 0,
    ) || 1

    subject.narratives = own.map((node, i) => {
      const kids = threadsByNarrative.get(node.id) ?? []
      // Leave a margin so a narrative disc never crosses its subject's edge — containment is
      // the contract the zoom depends on.
      const spot = phyllotaxis(i, own.length, subject.r * 0.62)
      const share = kids.length / totalThreads
      const point: NarrativePoint = {
        node,
        x: subject.x + spot.x,
        y: subject.y + spot.y,
        r: Math.max(RING_R * 0.02, subject.r * 0.30 * Math.sqrt(share) + RING_R * 0.016),
        threads: [],
      }
      point.threads = kids.map((t, j) => {
        const inner = phyllotaxis(j, kids.length, point.r * 0.78)
        const tp: ThreadPoint = {
          node: t,
          x: point.x + inner.x,
          y: point.y + inner.y,
          r: RING_R * 0.006
            + Math.sqrt(Math.min(1, (t.engagements ?? 0) / 5000)) * RING_R * 0.017,
        }
        threadById.set(t.id, tp)
        return tp
      })
      narrativeById.set(node.id, point)
      return point
    })
  }

  const subjectById = new Map(placed.map((s) => [s.node.id, s]))
  return { cx, cy, ringR, platforms, subjects: placed, subjectById, narrativeById, threadById }
}

/**
 * Push overlapping subject discs apart.
 *
 * This IS an O(n²) relaxation, and it is fine: it runs over subjects only — tens of them, not
 * thousands — and it runs once per payload rather than once per frame. Narratives and threads
 * never need it because phyllotaxis cannot produce an overlap in the first place.
 */
function relax(points: SubjectPoint[], cx: number, cy: number, ringR: number) {
  const gap = RING_R * 0.072
  for (let pass = 0; pass < 90; pass++) {
    let moved = 0
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const a = points[i]
        const b = points[j]
        if (!a || !b) continue
        const dx = b.x - a.x
        const dy = b.y - a.y
        const min = a.r + b.r + gap
        const d = Math.hypot(dx, dy) || 0.001
        if (d < min) {
          const push = (min - d) / d * 0.5
          a.x -= dx * push
          a.y -= dy * push
          b.x += dx * push
          b.y += dy * push
          moved += min - d
        }
      }
    }
    // Keep the whole arrangement inside the ring, so nothing hides behind a platform node.
    for (const p of points) {
      const dx = p.x - cx
      const dy = p.y - cy
      const d = Math.hypot(dx, dy) || 0.001
      const limit = ringR * 0.80 - p.r
      if (d > limit) {
        p.x = cx + (dx / d) * limit
        p.y = cy + (dy / d) * limit
      }
    }
    if (moved < 0.5) break
  }
}
